import { useQuery } from '@tanstack/react-query';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  ChartBarIcon as Activity,
  ExclamationCircleIcon as AlertCircle,
  TrophyIcon as Award,
  ChartBarIcon as BarChart3,
  BuildingOfficeIcon as Building,
  BuildingOffice2Icon as Building2,
  CheckCircleIcon as CheckCircle,
  ChevronRightIcon as ChevronRight,
  CurrencyDollarIcon as DollarSign,
  ArrowDownTrayIcon as Download,
  DocumentTextIcon as FileText,
  FunnelIcon as Filter,
  FlagIcon as Flag,
  PhotoIcon as ImageIcon,
  ListBulletIcon as ListTodo,
  MapPinIcon as MapPin,
  ChartPieIcon as PieChartIcon,
  ScaleIcon as Scale,
  MagnifyingGlassIcon as Search,
  ShieldCheckIcon as Shield,
  ArrowTrendingUpIcon as TrendingUp,
  XMarkIcon as X,
} from '@heroicons/react/24/outline';
import { GlassWater, Leaf, Target } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { buildFacilityRankingBarOption } from '../lib/facility-ranking-chart';
import CleanlinessLevel from '../components/CleanlinessLevel';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/Tabs';
import LocationActivityRanking from '../components/LocationActivityRanking';
import LocationRanking from '../components/LocationRanking';
import MetricCard from '../components/MetricCard';
import ObjectiveAnalytics from '../components/ObjectiveAnalytics';
import ActivityTrends from '../components/pages-specific/ActivityTrends';
import DecisionsPanel from '../components/pages-specific/DecisionsPanel';
import EnforcementPanel from '../components/pages-specific/EnforcementPanel';
import FacilityTypePerformanceTable from '../components/pages-specific/FacilityTypePerformanceTable';
import FollowupCompletion from '../components/pages-specific/FollowupCompletion';
import OverviewCards from '../components/pages-specific/OverviewCards';
import RecommendationsPanel from '../components/pages-specific/RecommendationsPanel';
import TopLocations from '../components/pages-specific/TopLocations';
import PieChart from '../components/PieChart';
import RwandaMap from '../components/RwandaMap';
import SingleLocationSelector from '../components/SingleLocationSelector';
import { Button } from '../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/Dialog';
import { Input } from '../components/ui/Input';
import useLocale from '../contexts/LocaleContext';
import {
  useAnalytics,
  useAssessmentKPIs,
  useQuestionnaireAnalytics,
} from '../hooks/useAnalytics';
import { useDashboardOverview } from '../hooks/useDashboard';
import { useFines, useFlaggedAssessments, useMarkFineCollected, useUpdateAssessmentFlag } from '../hooks/useAssessments';
import { useFacilityTypes } from '../hooks/useFacilities';
import { getCurrentUser } from '../hooks/useAuth';
import { hasPermission } from '../lib/permissions';
import { analyticsApi, facilitiesApi, questionsApi } from '../lib/api-client';
import { exportToExcel } from '../lib/excel-export';
import { cn } from '../lib/utils';
import type {
  AssessmentFineDto,
  AssessmentKPIsDto,
  AssessmentTrendDto,
  FacilityRegistrationAnalyticsDto,
  FacilityRegistrationLocationDto,
  FacilityTypeMetricsDto,
  FlaggedAssessmentDto,
  HygieneQuestionnaireListItemDto,
  LocationCleanlinessDto,
  OverallMetricsDto,
  QuestionAnalyticsDto,
} from '../types/api';
import ComplianceDashboard from './ComplianceDashboard';
import { BeverageAnalyticsPanel } from '../components/pages-specific/BeverageAnalyticsPanel';

type DashboardSection = 'hygiene' | 'compliance' | 'beverages';

type HygieneTab =
  | 'overview'
  | 'location-ranking'
  | 'activity-ranking'
  | 'objective-analytics'
  | 'flagged'
  | 'fines'
  | 'facility-registration'
  | 'decisions'
  | 'recommendations';

interface BreadcrumbItem {
  id: string;
  name: string;
  type: string;
}

const getLocalizedFacilityTypeName = (facilityTypeId: string, facilityTypes: any[], locale: string): string => {
  const facilityType = facilityTypes.find(ft => ft.id === facilityTypeId);
  if (!facilityType) return '';
  switch (locale) {
    case 'fr': return facilityType.nameFrench || facilityType.name;
    case 'rw': return facilityType.nameKinyarwanda || facilityType.name;
    default: return facilityType.name;
  }
};

const LOCATION_LEVEL_ORDER = ['PROVINCE', 'DISTRICT', 'SECTOR', 'CELL', 'VILLAGE'];

function FacilityLocationCell({ facilityId, fallback }: { facilityId: string; fallback: string }) {
  const { data: facility } = useQuery({
    queryKey: ['facility', facilityId],
    queryFn: () => facilitiesApi.getById(facilityId),
    staleTime: 5 * 60 * 1000,
  });

  const stack = facility?.locationStack;

  let fullPath: string | null = null;

  if (stack?.locations?.length) {
    const sorted = [...stack.locations].sort(
      (a, b) => LOCATION_LEVEL_ORDER.indexOf(a.locationType) - LOCATION_LEVEL_ORDER.indexOf(b.locationType)
    );
    fullPath = sorted.map(l => l.name).join(' / ');
  } else if (stack?.locationNames && stack.locationNames.length > 1) {
    fullPath = stack.locationNames.join(' / ');
  }

  const display = fullPath ?? facility?.location?.name ?? fallback;
  return <span title={display}>{display}</span>;
}

export default function Dashboard() {
  const { dictionary, locale } = useLocale();

  // Top-level section: hygiene or compliance
  const [section, setSection] = useState<DashboardSection>(() => {
    const p = new URLSearchParams(window.location.search);
    const s = p.get('section');
    return s === 'compliance' || s === 'beverages' ? s : 'hygiene';
  });

  // Hygiene sub-tab
  const [activeTab, setActiveTab] = useState<HygieneTab>(() => {
    const p = new URLSearchParams(window.location.search);
    const t = p.get('tab') as HygieneTab;
    return ['overview', 'location-ranking', 'activity-ranking', 'objective-analytics', 'flagged', 'fines', 'facility-registration', 'decisions', 'recommendations'].includes(t) ? t : 'overview';
  });

  // Sync section + tab to URL
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (section === 'compliance' || section === 'beverages') {
      p.set('section', section);
      p.delete('tab');
    } else {
      p.delete('section');
      if (activeTab !== 'overview') p.set('tab', activeTab);
      else p.delete('tab');
    }
    const url = `${window.location.pathname}${p.toString() ? `?${p.toString()}` : ''}`;
    window.history.replaceState({}, '', url);
  }, [section, activeTab]);

  // Hygiene filters
  const [filters, setFilters] = useState<{ month?: string; facilityTypeId?: string; geoLocationId?: string }>({});
  const [locationResetKey, setLocationResetKey] = useState(0);

  const currentMonthLabel = useMemo(() => {
    if (!filters.month) return 'All time';
    return new Date(`${filters.month}-01`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [filters.month]);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<string>('');
  const [questionnaireFilters, setQuestionnaireFilters] = useState<{ locationId?: string; startDate?: string; endDate?: string }>({});
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [exportLoading, setExportLoading] = useState<string | null>(null);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const [exportingFacilityResponses, setExportingFacilityResponses] = useState(false);
  const [questionBreakdownByQuestionId, setQuestionBreakdownByQuestionId] = useState<Record<string, {
    questionText: string;
    byOption: Record<string, Array<{ facilityName: string; locationName: string; assessmentDate: string; score: number }>>;
  }>>({});
  const [loadingQuestionBreakdownId, setLoadingQuestionBreakdownId] = useState<string | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<{ question: QuestionAnalyticsDto; index: number } | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(e.target as Node)) {
        setShowExportDropdown(false);
      }
    };
    if (showExportDropdown) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportDropdown]);

  // Data hooks (hygiene)
  const { data: analytics, isLoading: analyticsLoading, error: analyticsError } = useAnalytics();
  const { data: overviewData, isLoading: overviewLoading } = useDashboardOverview({
    month: filters.month,
    type: 'HYGIENE',
    facilityTypeId: filters.facilityTypeId,
    geoLocationId: filters.geoLocationId,
  });
  const { data: facilityTypes = [] } = useFacilityTypes();
  const currentUser = useMemo(() => getCurrentUser(), []);
  const allowedFacilityTypeIds = currentUser?.allowedFacilityTypeIds;

  const { data: allHygieneQuestionnaires = [] } = useQuery<HygieneQuestionnaireListItemDto[]>({
    queryKey: ['hygiene-questionnaires'],
    queryFn: () => questionsApi.listHygieneQuestionnaires(),
  });

  const questionnaires = useMemo(() => {
    if (!allowedFacilityTypeIds?.length) return allHygieneQuestionnaires;
    return allHygieneQuestionnaires.filter(
      q => !q.facilityTypeId || allowedFacilityTypeIds.includes(q.facilityTypeId)
    );
  }, [allHygieneQuestionnaires, allowedFacilityTypeIds]);

  const { data: questionnaireAnalytics, isLoading: questionnaireAnalyticsLoading } = useQuestionnaireAnalytics(
    selectedQuestionnaireId,
    questionnaireFilters
  );
  const [finesTypeTab, setFinesTypeTab] = useState<'ALL' | 'HYGIENE' | 'COMPLIANCE'>('ALL');
  const [finesPage, setFinesPage] = useState(1);
  const [finesSearch, setFinesSearch] = useState('');
  const [finesSearchInput, setFinesSearchInput] = useState('');
  const finesLimit = 20;
  const { data: finesData, isLoading: finesLoading } = useFines({
    assessmentType: finesTypeTab === 'ALL' ? undefined : finesTypeTab,
    page: finesPage,
    limit: finesLimit,
    search: finesSearch || undefined,
  });
  const finesList = finesData?.data ?? [];
  const finesPagination = finesData?.pagination;
  const markFineCollected = useMarkFineCollected();
  const [collectConfirm, setCollectConfirm] = useState<AssessmentFineDto | null>(null);

  const [kpiType, setKpiType] = useState<'HYGIENE' | 'COMPLIANCE'>('HYGIENE');
  const { data: kpiData, isLoading: kpiLoading } = useAssessmentKPIs({
    type: kpiType,
    facilityTypeId: filters.facilityTypeId,
  });

  // Facility registration drill-down + filters
  const [facilityRegStack, setFacilityRegStack] = useState<{ id: string; name: string }[]>([]);
  const facilityRegParentId = facilityRegStack.length > 0 ? facilityRegStack[facilityRegStack.length - 1].id : undefined;
  const [facilityRegFacilityTypeId, setFacilityRegFacilityTypeId] = useState('');
  const [facilityRegLocationType, setFacilityRegLocationType] = useState('');
  const [facilityRegStartDate, setFacilityRegStartDate] = useState('');
  const [facilityRegEndDate, setFacilityRegEndDate] = useState('');
  const [facilityRegModalLocation, setFacilityRegModalLocation] = useState<FacilityRegistrationLocationDto | null>(null);
  const [facilityRegModalTypeFilter, setFacilityRegModalTypeFilter] = useState('');
  const [facilityRegModalSearch, setFacilityRegModalSearch] = useState('');
  const [facilityRegModalPage, setFacilityRegModalPage] = useState(1);

  useEffect(() => {
    setFacilityRegModalTypeFilter('');
    setFacilityRegModalSearch('');
    setFacilityRegModalPage(1);
  }, [facilityRegModalLocation?.locationId]);

  const { data: facilityRegData, isLoading: facilityRegLoading } = useQuery<FacilityRegistrationAnalyticsDto>({
    queryKey: ['facility-registration', facilityRegParentId, facilityRegFacilityTypeId, facilityRegLocationType, facilityRegStartDate, facilityRegEndDate],
    queryFn: () => analyticsApi.getFacilityRegistration({
      parentLocationId: facilityRegParentId,
      facilityTypeId: facilityRegFacilityTypeId || undefined,
      locationType: facilityRegLocationType || undefined,
      startDate: facilityRegStartDate || undefined,
      endDate: facilityRegEndDate || undefined,
    }),
    enabled: activeTab === 'facility-registration',
  });

  const { data: facilityRegFacilities, isLoading: facilityRegFacilitiesLoading } = useQuery({
    queryKey: ['facility-reg-list', facilityRegModalLocation?.locationId, facilityRegModalTypeFilter, facilityRegModalSearch, facilityRegModalPage],
    queryFn: () => facilitiesApi.getAll({
      locationId: facilityRegModalLocation!.locationId,
      facilityTypeId: facilityRegModalTypeFilter || undefined,
      search: facilityRegModalSearch || undefined,
      page: facilityRegModalPage,
      limit: 15,
    }),
    enabled: !!facilityRegModalLocation,
  });

  const [flaggedTypeFilter, setFlaggedTypeFilter] = useState<'HYGIENE' | 'COMPLIANCE' | undefined>(undefined);
  const [flaggedLocationId, setFlaggedLocationId] = useState<string | undefined>(undefined);
  const [flaggedPage, setFlaggedPage] = useState(1);
  const { data: flaggedData, isLoading: flaggedLoading } = useFlaggedAssessments({
    assessmentType: flaggedTypeFilter,
    locationId: flaggedLocationId,
    page: flaggedPage,
    limit: 20,
  });
  const updateFlagMutation = useUpdateAssessmentFlag();

  useEffect(() => {
    if (questionnaires && questionnaires.length > 0 && !selectedQuestionnaireId) {
      setSelectedQuestionnaireId(questionnaires[0].id);
    }
  }, [questionnaires, selectedQuestionnaireId]);

  useEffect(() => {
    setQuestionBreakdownByQuestionId({});
    setLoadingQuestionBreakdownId(null);
  }, [selectedQuestionnaireId]);

  const facilityTypeMetrics: FacilityTypeMetricsDto[] = analytics?.facilityTypeMetrics || [];

  const clearFilters = useCallback(() => { setFilters({}); setLocationResetKey(k => k + 1); }, []);

  const handleQuestionnaireFilterChange = useCallback((key: string, value: any) => {
    setQuestionnaireFilters(prev => ({ ...prev, [key]: value || undefined }));
  }, []);

  const clearQuestionnaireFilters = useCallback(() => setQuestionnaireFilters({}), []);

  const hasQuestionnaireFilters = useMemo(
    () => Object.values(questionnaireFilters).some(v => v !== undefined),
    [questionnaireFilters]
  );

  // Export handlers
  const handleExportFacilityTypesExcel = useCallback(() => {
    if (!facilityTypeMetrics.length) return;
    setExportLoading('excel');
    setShowExportDropdown(false);
    try {
      exportToExcel({
        fileName: 'Facility_Type_Performance',
        sheetName: 'Performance',
        data: facilityTypeMetrics.map(type => ({
          facilityTypeName: getLocalizedFacilityTypeName(type.facilityTypeId, facilityTypes, locale),
          totalFacilities: type.totalFacilities,
          totalAssessments: type.totalAssessments,
          averageScore: type.totalAssessments === 0 ? '-' : `${type.averageScore.toFixed(1)}%`,
        })),
        columns: [
          { key: 'facilityTypeName', label: 'Facility Type' },
          { key: 'totalFacilities', label: 'Total Facilities' },
          { key: 'totalAssessments', label: 'Total Assessments' },
          { key: 'averageScore', label: 'Average Score' },
        ],
        title: 'NHIP - Facility Type Performance',
        subtitle: `Dashboard Overview | Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
      });
    } catch (e) { console.error(e); } finally { setExportLoading(null); }
  }, [facilityTypeMetrics, facilityTypes, locale]);

  const handleExportFacilityTypesPDF = useCallback(() => {
    if (!facilityTypeMetrics.length) return;
    setExportLoading('pdf');
    setShowExportDropdown(false);
    try {
      const doc = new jsPDF();
      doc.setFontSize(16); doc.setFont('helvetica', 'bold');
      doc.text('NHIP - Facility Type Performance', 20, 20);
      doc.setFontSize(12); doc.setFont('helvetica', 'normal');
      doc.text(`Dashboard Overview | ${new Date().toLocaleDateString()}`, 20, 30);
      autoTable(doc, {
        startY: 45,
        head: [['Facility Type', 'Total Facilities', 'Assessments', 'Avg Score']],
        body: facilityTypeMetrics.map(type => [
          getLocalizedFacilityTypeName(type.facilityTypeId, facilityTypes, locale),
          type.totalFacilities.toString(),
          type.totalAssessments.toString(),
          type.totalAssessments === 0 ? '-' : `${type.averageScore.toFixed(1)}%`,
        ]),
        margin: { left: 20, right: 20 },
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [59, 130, 246] },
      });
      doc.save(`Facility_Type_Performance_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (e) { console.error(e); } finally { setExportLoading(null); }
  }, [facilityTypeMetrics, facilityTypes, locale]);

  const handleExportQuestionnaireAnalytics = useCallback(() => {
    if (!questionnaireAnalytics?.questions.length) return;
    exportToExcel({
      fileName: `Questionnaire_Analytics_${questionnaireAnalytics.questionnaireName.replace(/\s+/g, '_')}`,
      sheetName: 'Analytics',
      data: questionnaireAnalytics.questions.flatMap((q: QuestionAnalyticsDto, qi: number) =>
        q.options.map(opt => ({
          questionNumber: qi + 1, questionText: q.questionText, questionType: q.questionType,
          questionWeight: q.weight, optionText: opt.optionText, optionMarks: opt.marks,
          selectionCount: opt.selectionCount, totalResponses: q.totalResponses,
          percentage: `${opt.percentage.toFixed(1)}%`,
        }))
      ),
      columns: [
        { key: 'questionNumber', label: 'Q#' }, { key: 'questionText', label: 'Question' },
        { key: 'questionType', label: 'Type' }, { key: 'questionWeight', label: 'Weight' },
        { key: 'optionText', label: 'Option' }, { key: 'optionMarks', label: 'Marks' },
        { key: 'selectionCount', label: 'Selections' }, { key: 'totalResponses', label: 'Total Responses' },
        { key: 'percentage', label: 'Percentage' },
      ],
      title: `NHIP - ${questionnaireAnalytics.questionnaireName}`,
      subtitle: `${questionnaireAnalytics.facilityTypeName} | ${questionnaireAnalytics.totalAssessments} assessments`,
    });
  }, [questionnaireAnalytics]);

  const handleExportFacilityResponses = useCallback(async () => {
    if (!selectedQuestionnaireId) return;
    setExportingFacilityResponses(true);
    try {
      const data = await analyticsApi.getFacilityQuestionnaireResponses(
        selectedQuestionnaireId,
        questionnaireFilters
      );
      exportToExcel({
        fileName: `Facility_Responses_${data.questionnaireName.replace(/\s+/g, '_')}`,
        sheetName: 'Facility Responses',
        data: data.responses.map(r => ({
          facility: r.facilityName,
          location: r.locationName,
          date: new Date(r.assessmentDate).toLocaleDateString(),
          score: `${r.score.toFixed(1)}%`,
          assessorName: r.assessorName || '—',
          assessorEmail: r.assessorEmail || '—',
          assessorRole: r.assessorRole || '—',
          ...data.questions.reduce<Record<string, string>>(
            (acc, _q, i) => ({ ...acc, [`q${i + 1}`]: r.answers[i] ?? '—' }),
            {}
          ),
        })),
        columns: [
          { key: 'facility', label: 'Facility' },
          { key: 'location', label: 'Location' },
          { key: 'date', label: 'Assessment Date' },
          { key: 'score', label: 'Score' },
          { key: 'assessorName', label: 'Assessed By' },
          { key: 'assessorEmail', label: 'Assessor Email' },
          { key: 'assessorRole', label: 'Assessor Role' },
          ...data.questions.map((q, i) => ({ key: `q${i + 1}`, label: q })),
        ],
        title: `NHIP - ${data.questionnaireName} - Facility Responses`,
        subtitle: `${data.facilityTypeName} | ${data.responses.length} assessments`,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setExportingFacilityResponses(false);
    }
  }, [selectedQuestionnaireId, questionnaireFilters]);

  const handleGenerateQuestionBreakdown = useCallback(async (questionId: string) => {
    if (!selectedQuestionnaireId) return;
    setLoadingQuestionBreakdownId(questionId);
    setQuestionBreakdownByQuestionId(prev => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
    try {
      const data = await analyticsApi.getQuestionFacilityResponses(
        selectedQuestionnaireId,
        questionId,
        questionnaireFilters,
      );
      const byOption: Record<string, Array<{ facilityName: string; locationName: string; assessmentDate: string; score: number }>> = {};
      for (const opt of data.optionResponses) {
        byOption[opt.optionText] = opt.facilities.map(f => ({
          facilityName: f.facilityName,
          locationName: f.locationName,
          assessmentDate: f.assessmentDate,
          score: f.score,
        }));
      }
      setQuestionBreakdownByQuestionId(prev => ({
        ...prev,
        [questionId]: { questionText: data.questionText, byOption },
      }));
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingQuestionBreakdownId(null);
    }
  }, [selectedQuestionnaireId, questionnaireFilters]);

  const isYesOption = (answer: string) => /^(yes|oui|yego)$/i.test(answer.trim());
  const handleExportQuestionBreakdown = useCallback((questionId: string) => {
    const breakdown = questionBreakdownByQuestionId[questionId];
    if (!breakdown || !questionnaireAnalytics?.questionnaireName) return;
    const rows: { facility: string; location: string; date: string; score: string; yes: string; no: string }[] = [];
    for (const [response, facilities] of Object.entries(breakdown.byOption)) {
      const tickYes = isYesOption(response) ? '✓' : '';
      const tickNo = !isYesOption(response) ? '✓' : '';
      for (const f of facilities) {
        rows.push({
          facility: f.facilityName,
          location: f.locationName,
          date: new Date(f.assessmentDate).toLocaleDateString(),
          score: `${f.score.toFixed(1)}%`,
          yes: tickYes,
          no: tickNo,
        });
      }
    }
    exportToExcel({
      fileName: `Question_Yes_No_${questionnaireAnalytics.questionnaireName.replace(/\s+/g, '_')}`,
      sheetName: 'Who responded',
      data: rows,
      columns: [
        { key: 'facility', label: dictionary['dashboard.fines_facility'] ?? 'Facility' },
        { key: 'location', label: dictionary['dashboard.fines_location'] ?? 'Location' },
        { key: 'date', label: dictionary['dashboard.fines_date'] ?? 'Date' },
        { key: 'score', label: dictionary['dashboard.fines_score'] ?? 'Score' },
        { key: 'yes', label: 'Yes' },
        { key: 'no', label: 'No' },
      ],
      title: `NHIP - Who responded Yes/No`,
      subtitle: breakdown.questionText.length > 80 ? breakdown.questionText.slice(0, 80) + '…' : breakdown.questionText,
    });
  }, [questionBreakdownByQuestionId, questionnaireAnalytics, dictionary]);

  const getPercentageColor = useCallback((p: number) =>
    p >= 80 ? 'text-green-600' : p >= 60 ? 'text-yellow-600' : p >= 40 ? 'text-orange-600' : 'text-red-600', []);
  const getBarColor = useCallback((p: number) =>
    p >= 80 ? 'bg-green-500' : p >= 60 ? 'bg-yellow-500' : p >= 40 ? 'bg-orange-500' : 'bg-red-500', []);

  // Loading / error gates (hygiene only)
  if (section === 'hygiene' && analyticsLoading) {
    return (
      <div className='p-4 sm:p-6'>
        <div className='text-center py-12'>
          <h3 className='text-base sm:text-lg font-medium text-gray-900 mb-3'>{dictionary['dashboard.loading_analytics']}</h3>
          <div className='mx-auto h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-primary mb-3' />
          <p className='text-sm text-gray-500'>{dictionary['dashboard.please_wait']}</p>
        </div>
      </div>
    );
  }
  if (section === 'hygiene' && analyticsError) {
    return (
      <div className='p-4 sm:p-6'>
        <div className='text-center py-8 text-red-500'>
          {dictionary['dashboard.error_loading']}: {analyticsError.message}
        </div>
      </div>
    );
  }
  if (section === 'hygiene' && (!analytics || (!analytics.overallMetrics?.totalFacilities && !analytics.overallMetrics?.totalAssessments))) {
    return (
      <div className='p-4 sm:p-6'>
        <div className='text-center py-12'>
          <div className='text-gray-400 mb-4'><BarChart3 className='h-12 w-12 sm:h-16 sm:w-16 mx-auto' /></div>
          <h3 className='text-base sm:text-lg font-medium text-gray-900 mb-2'>{dictionary['dashboard.no_analytics_data']}</h3>
          <p className='text-sm text-gray-500 mb-4 px-4'>{dictionary['dashboard.no_data_message']}</p>
          <div className='flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4'>
            <Button onClick={() => (window.location.href = '/facilities')} className='w-full sm:w-auto'>{dictionary['dashboard.view_facilities']}</Button>
            <Button onClick={() => (window.location.href = '/assessments/new')} variant='outline' className='w-full sm:w-auto'>{dictionary['dashboard.create_assessment']}</Button>
          </div>
        </div>
      </div>
    );
  }

  const overallMetrics: OverallMetricsDto = analytics?.overallMetrics || {
    totalFacilities: 0, totalAssessments: 0, averageScore: 0,
    cleanFacilities: 0, uncleanFacilities: 0, cleanlinessPercentage: 0,
    assessmentsThisMonth: 0, assessmentsLastMonth: 0, monthOverMonthGrowth: 0,
  };
  const locationCleanliness: LocationCleanlinessDto[] = analytics?.locationCleanliness || [];
  const assessmentTrends: AssessmentTrendDto[] = analytics?.assessmentTrends || [];

  return (
    <div className='min-h-screen bg-[var(--nhip-surface)]'>
      {/* ── Page Header ── */}
      <div className='bg-white border-b border-[var(--nhip-border)] px-4 sm:px-6 py-5'>
       <div className='max-w-[1600px] mx-auto'>
        <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4'>
          <div>
            <h1 className='text-2xl font-bold text-foreground'>{dictionary['nav.dashboard']}</h1>
            <p className='text-sm text-muted-foreground mt-0.5'>
              Hygiene &amp; compliance inspection metrics · {currentMonthLabel}
            </p>
          </div>

          {/* Hygiene / Compliance / Beverages toggle */}
          <Tabs value={section} onValueChange={v => setSection(v as 'hygiene' | 'compliance' | 'beverages')} className='self-start'>
            <TabsList>
              <TabsTrigger value='hygiene'>
                <Leaf className='h-3.5 w-3.5' />
                {dictionary['dashboard.hygiene'] ?? 'Hygiene'}
              </TabsTrigger>
              <TabsTrigger value='compliance'>
                <Shield className='h-3.5 w-3.5' />
                {dictionary['dashboard.compliance'] ?? 'Compliance'}
              </TabsTrigger>
              {hasPermission(currentUser, 'BEVERAGE_AUTHORIZATIONS_READ') && (
                <TabsTrigger value='beverages'>
                  <GlassWater className='h-3.5 w-3.5' />
                  {dictionary['dashboard.beverages'] ?? 'Beverages'}
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
        </div>

        {/* Filter bar — hygiene overview only */}
        {section === 'hygiene' && activeTab === 'overview' && (
          <div className='mt-3 flex flex-wrap items-center gap-2'>
            <Filter className='h-3.5 w-3.5 flex-shrink-0' style={{ color: 'var(--nhip-ink-muted)' }} />

            {/* Month */}
            <Input
              type='month'
              value={filters.month || ''}
              onChange={e => setFilters(prev => ({ ...prev, month: e.target.value || undefined }))}
              className='w-auto text-xs'
              style={{ colorScheme: 'light' }}
            />

            {/* Facility Type */}
            <Input
              type='select'
              value={filters.facilityTypeId || ''}
              onChange={e => setFilters(prev => ({ ...prev, facilityTypeId: (e.target.value as string) || undefined }))}
              placeholder={dictionary['dashboard.all_facility_types'] ?? 'All Facility Types'}
              options={facilityTypes.map(ft => ({ value: ft.id, label: getLocalizedFacilityTypeName(ft.id, facilityTypes, locale) }))}
              className='w-auto min-w-[150px]'
            />

            {/* Location — full hierarchy drilldown */}
            <SingleLocationSelector
              key={locationResetKey}
              compact
              selectedLocationId={filters.geoLocationId}
              onLocationChange={id => setFilters(prev => ({ ...prev, geoLocationId: id || undefined }))}
            />

            {/* Active filter chips */}
            {filters.month && (
              <span className='inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-xs font-medium'
                style={{ backgroundColor: 'rgba(37,99,235,0.08)', color: '#2563eb' }}>
                {currentMonthLabel}
                <button onClick={() => setFilters(prev => ({ ...prev, month: undefined }))} className='hover:opacity-70'>
                  <X className='h-3 w-3' />
                </button>
              </span>
            )}

            {/* All time indicator */}
            {!filters.month && (
              <span className='text-xs font-medium' style={{ color: 'var(--nhip-green)' }}>· All time</span>
            )}

            {/* Clear all */}
            {(filters.month || filters.facilityTypeId || filters.geoLocationId) && (
              <button
                onClick={clearFilters}
                className='ml-1 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors'
              >
                <X className='h-3 w-3' />Clear
              </button>
            )}
          </div>
        )}
       </div>
      </div>

      <div className='p-4 sm:p-6 space-y-3 sm:space-y-4 max-w-[1600px] mx-auto'>
      {/* ── COMPLIANCE SECTION ── */}
      {section === 'compliance' && <ComplianceDashboard />}

      {/* ── BEVERAGES SECTION ── */}
      {section === 'beverages' && <BeverageAnalyticsPanel />}

      {/* ── HYGIENE SECTION ── */}
      {section === 'hygiene' && (
        <>
          {/* Hygiene sub-tabs */}
          <div className='border-b border-[var(--nhip-border)] -mx-4 sm:-mx-6 px-4 sm:px-6 overflow-x-auto'>
            <nav className='-mb-px flex space-x-6 min-w-max'>
              {([
                { id: 'overview' as HygieneTab, label: 'Overview', icon: TrendingUp },
                { id: 'location-ranking' as HygieneTab, label: dictionary['dashboard.location_ranking'] ?? 'Location Ranking', icon: Award },
                { id: 'activity-ranking' as HygieneTab, label: dictionary['dashboard.activity_ranking'] ?? 'Activity', icon: Activity },
                { id: 'objective-analytics' as HygieneTab, label: dictionary['common.objectives'] ?? 'Objectives', icon: Target },
                { id: 'flagged' as HygieneTab, label: dictionary['dashboard.flagged'] ?? 'Flagged', icon: Flag, count: flaggedData?.total },
                { id: 'fines' as HygieneTab, label: dictionary['dashboard.fines'] ?? 'Fines', icon: Scale },
                { id: 'facility-registration' as HygieneTab, label: dictionary['dashboard.facility_registration'] ?? 'Facility Registration', icon: Building2 },
                { id: 'decisions' as HygieneTab, label: 'Decisions', icon: Scale },
                { id: 'recommendations' as HygieneTab, label: 'Recommendations', icon: ListTodo },
              ] as const).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'py-3 px-1 border-b-[3px] font-medium text-sm whitespace-nowrap flex items-center gap-1.5 transition-colors duration-150',
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  <tab.icon className='w-4 h-4' />
                  {tab.label}
                  {'count' in tab && tab.count != null && tab.count > 0 && (
                    <span className='text-xs font-semibold text-muted-foreground'>{tab.count}</span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Location Ranking */}
          {activeTab === 'location-ranking' && (
            <div className='-mx-4 sm:-mx-6'>
              <LocationRanking />
            </div>
          )}

          {/* Activity Ranking */}
          {activeTab === 'activity-ranking' && (
            <div className='-mx-4 sm:-mx-6'>
              <LocationActivityRanking type='HYGIENE' />
            </div>
          )}

          {/* Objectives */}
          {activeTab === 'objective-analytics' && <ObjectiveAnalytics />}

          {/* Flagged assessments */}
          {activeTab === 'flagged' && (
            <div className='bg-white rounded-lg border border-gray-200 p-4 sm:p-6'>
              <h3 className='text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2'>
                <Flag className='h-4 w-4 text-amber-600' />
                {dictionary['dashboard.flagged'] ?? 'Flagged Assessments'}
              </h3>
              {/* Location filter */}
              <div className='mb-4'>
                <SingleLocationSelector
                  selectedLocationId={flaggedLocationId}
                  onLocationChange={(id) => { setFlaggedLocationId(id || undefined); setFlaggedPage(1); }}
                />
              </div>

              {/* byType chips — always show both counts */}
              {flaggedData?.byType != null && (
                <div className='flex flex-wrap gap-2 mb-4'>
                  <button
                    onClick={() => { setFlaggedTypeFilter(undefined); setFlaggedPage(1); }}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium ${flaggedTypeFilter == null ? 'bg-secondary text-primary' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {dictionary['common.all'] ?? 'All'} ({(flaggedData.byType.HYGIENE ?? 0) + (flaggedData.byType.COMPLIANCE ?? 0)})
                  </button>
                  <button
                    onClick={() => { setFlaggedTypeFilter('HYGIENE'); setFlaggedPage(1); }}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium ${flaggedTypeFilter === 'HYGIENE' ? 'bg-secondary text-primary' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {dictionary['assessment_detail.type_hygiene'] ?? 'Hygiene'} ({flaggedData.byType.HYGIENE ?? 0})
                  </button>
                  <button
                    onClick={() => { setFlaggedTypeFilter('COMPLIANCE'); setFlaggedPage(1); }}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium ${flaggedTypeFilter === 'COMPLIANCE' ? 'bg-secondary text-primary' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {dictionary['assessment_detail.type_compliance'] ?? 'Compliance'} ({flaggedData.byType.COMPLIANCE ?? 0})
                  </button>
                </div>
              )}

              {flaggedLoading ? (
                <div className='flex justify-center py-12'><div className='h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-primary' /></div>
              ) : !flaggedData?.data?.length ? (
                <p className='text-sm text-gray-500 text-center py-8'>{dictionary['dashboard.no_data_message'] ?? 'No flagged assessments.'}</p>
              ) : (
                <div className='overflow-x-auto'>
                  <table className='w-full text-sm'>
                    <thead>
                      <tr className='border-b border-gray-200 text-left text-gray-600'>
                        <th className='pb-2 pr-4 font-medium'>{dictionary['dashboard.fines_facility'] ?? 'Facility'}</th>
                        <th className='pb-2 pr-4 font-medium hidden sm:table-cell'>{dictionary['dashboard.fines_location'] ?? 'Location'}</th>
                        <th className='pb-2 pr-4 font-medium'>{dictionary['dashboard.facility_type_label'] ?? 'Type'}</th>
                        <th className='pb-2 pr-4 font-medium'>{dictionary['dashboard.fines_score'] ?? 'Score'}</th>
                        <th className='pb-2 pr-4 font-medium'>{dictionary['dashboard.fines_date'] ?? 'Date'}</th>
                        <th className='pb-2 pl-2 font-medium'>{dictionary['common.actions'] ?? 'Actions'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {flaggedData.data.map((item: FlaggedAssessmentDto) => (
                        <tr key={item.id} className='border-b border-gray-100 hover:bg-gray-50'>
                          <td className='py-3 pr-4 font-medium text-gray-900'>
                            <a href={`/assessments/${item.id}`} className='text-primary hover:underline'>{item.facilityName}</a>
                          </td>
                          <td className='py-3 pr-4 text-gray-600 hidden sm:table-cell max-w-[240px]'>
                            <FacilityLocationCell facilityId={item.facilityId} fallback={item.locationName} />
                          </td>
                          <td className='py-3 pr-4 text-gray-600'>{item.facilityTypeName}</td>
                          <td className='py-3 pr-4'>
                            <span className={item.percentageScore >= 50 ? 'text-green-600' : 'text-red-600'}>{item.percentageScore.toFixed(1)}%</span>
                          </td>
                          <td className='py-3 pr-4 text-gray-600'>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—'}</td>
                          <td className='py-3 pl-2'>
                            <button
                              onClick={() => updateFlagMutation.mutate({ id: item.id, isFlagged: false })}
                              disabled={updateFlagMutation.isPending}
                              className='text-amber-600 hover:text-amber-800 text-xs font-medium'
                            >
                              {dictionary['dashboard.unflag'] ?? 'Unflag'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {flaggedData && flaggedData.total > 20 && (
                <div className='mt-4 flex items-center justify-between text-sm text-gray-600'>
                  <span>
                    {dictionary['common.showing'] ?? 'Showing'} {(flaggedPage - 1) * 20 + 1}–{Math.min(flaggedPage * 20, flaggedData.total)} {dictionary['common.of'] ?? 'of'} {flaggedData.total}
                  </span>
                  <div className='flex gap-2'>
                    <button
                      onClick={() => setFlaggedPage(p => Math.max(1, p - 1))}
                      disabled={flaggedPage <= 1}
                      className='px-2 py-1 rounded border border-gray-300 disabled:opacity-50'
                    >
                      {dictionary['common.previous'] ?? 'Previous'}
                    </button>
                    <button
                      onClick={() => setFlaggedPage(p => p + 1)}
                      disabled={flaggedPage * 20 >= flaggedData.total}
                      className='px-2 py-1 rounded border border-gray-300 disabled:opacity-50'
                    >
                      {dictionary['common.next'] ?? 'Next'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Fines */}
          {activeTab === 'fines' && (
            <div className='bg-white rounded-lg border border-gray-200 p-4 sm:p-6 space-y-4'>
              <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
                <h3 className='text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2'>
                  <DollarSign className='h-4 w-4 text-amber-600' />
                  {dictionary['dashboard.fines'] ?? 'Assessments with Fines'}
                </h3>
                <div className='flex flex-col sm:flex-row items-start sm:items-center gap-2'>
                  {/* Search */}
                  <form onSubmit={e => { e.preventDefault(); setFinesSearch(finesSearchInput); setFinesPage(1); }} className='flex gap-1'>
                    <input
                      type='text'
                      value={finesSearchInput}
                      onChange={e => setFinesSearchInput(e.target.value)}
                      placeholder='Search facility, NID, phone…'
                      className='h-8 px-3 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary w-48 sm:w-56'
                    />
                    <button type='submit'
                      className='h-8 px-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-500 hover:text-gray-700'>
                      <Search className='h-3.5 w-3.5' />
                    </button>
                    {finesSearch && (
                      <button type='button' onClick={() => { setFinesSearch(''); setFinesSearchInput(''); setFinesPage(1); }}
                        className='h-8 px-2 text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg'>
                        ✕
                      </button>
                    )}
                  </form>
                  {/* Type tabs */}
                  <div className='flex gap-1 bg-gray-100 rounded-lg p-1'>
                    {(['ALL', 'HYGIENE', 'COMPLIANCE'] as const).map(t => (
                      <button key={t} onClick={() => { setFinesTypeTab(t); setFinesPage(1); }}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${finesTypeTab === t ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
                        {t === 'ALL' ? 'All' : t.charAt(0) + t.slice(1).toLowerCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Stat cards */}
              {finesData?.stats && (
                <div className='grid grid-cols-3 gap-3'>
                  <div className='rounded-xl border border-gray-200 bg-gray-50 p-3 sm:p-4'>
                    <p className='text-xs text-gray-500 mb-1'>Total Fines</p>
                    <p className='text-base sm:text-xl font-bold text-gray-900'>
                      {finesData.stats.totalAmount.toLocaleString()} <span className='text-xs font-normal text-gray-400'>RWF</span>
                    </p>
                    <p className='text-xs text-gray-400 mt-0.5'>{finesData.stats.totalCount.toLocaleString()} assessments</p>
                  </div>
                  <div className='rounded-xl border border-red-100 bg-red-50 p-3 sm:p-4'>
                    <p className='text-xs text-red-500 mb-1'>Outstanding</p>
                    <p className='text-base sm:text-xl font-bold text-red-600'>
                      {finesData.stats.outstandingAmount.toLocaleString()} <span className='text-xs font-normal text-red-400'>RWF</span>
                    </p>
                    <p className='text-xs text-red-400 mt-0.5'>
                      {finesData.stats.totalAmount > 0
                        ? `${((finesData.stats.outstandingAmount / finesData.stats.totalAmount) * 100).toFixed(0)}% of total`
                        : '0% of total'}
                    </p>
                  </div>
                  <div className='rounded-xl border border-green-100 bg-green-50 p-3 sm:p-4'>
                    <p className='text-xs text-green-600 mb-1'>Collected</p>
                    <p className='text-base sm:text-xl font-bold text-green-700'>
                      {finesData.stats.collectedAmount.toLocaleString()} <span className='text-xs font-normal text-green-400'>RWF</span>
                    </p>
                    <p className='text-xs text-green-500 mt-0.5'>
                      {finesData.stats.totalAmount > 0
                        ? `${((finesData.stats.collectedAmount / finesData.stats.totalAmount) * 100).toFixed(0)}% of total`
                        : '0% of total'}
                    </p>
                  </div>
                </div>
              )}

              {finesLoading ? (
                <div className='flex justify-center py-12'><div className='h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-primary' /></div>
              ) : finesList.length === 0 ? (
                <p className='text-sm text-gray-500 text-center py-8'>{dictionary['dashboard.no_fines'] ?? 'No assessments with fines.'}</p>
              ) : (
                <>
                  <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                      <thead>
                        <tr className='border-b border-gray-200 text-left text-gray-600'>
                          <th className='pb-2 pr-4 font-medium'>Facility</th>
                          <th className='pb-2 pr-4 font-medium hidden sm:table-cell'>Location</th>
                          <th className='pb-2 pr-4 font-medium'>Type</th>
                          <th className='pb-2 pr-4 font-medium'>Amount</th>
                          <th className='pb-2 pr-4 font-medium'>Score</th>
                          <th className='pb-2 pr-4 font-medium'>Date</th>
                          <th className='pb-2 pr-4 font-medium'>Status</th>
                          <th className='pb-2 pl-2 font-medium'>Pictures</th>
                        </tr>
                      </thead>
                      <tbody>
                        {finesList.map((item: AssessmentFineDto) => (
                          <tr key={item.id} className='border-b border-gray-100 hover:bg-gray-50'>
                            <td className='py-3 pr-4 font-medium text-gray-900'>
                              <a href={`/assessments/${item.id}`} className='text-primary hover:underline'>{item.facilityTitle}</a>
                            </td>
                            <td className='py-3 pr-4 text-gray-600 hidden sm:table-cell max-w-[160px] truncate'>{item.location}</td>
                            <td className='py-3 pr-4'>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${item.assessmentType === 'HYGIENE' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                {item.assessmentType}
                              </span>
                            </td>
                            <td className='py-3 pr-4'>
                              <span className='font-medium text-amber-700'>{item.fineAmount?.toLocaleString?.() ?? item.fineAmount}</span>
                            </td>
                            <td className='py-3 pr-4'>
                              <span className={item.score >= 50 ? 'text-green-600' : 'text-red-600'}>{item.score.toFixed(1)}%</span>
                            </td>
                            <td className='py-3 pr-4 text-gray-600'>{item.date ? new Date(item.date).toLocaleDateString() : '—'}</td>
                            <td className='py-3 pr-4'>
                              <button
                                onClick={() => item.isCollected
                                  ? markFineCollected.mutate({ id: item.id, collected: false })
                                  : setCollectConfirm(item)
                                }
                                disabled={markFineCollected.isPending}
                                className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border transition-colors ${item.isCollected ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'}`}>
                                {item.isCollected ? 'Collected' : 'Outstanding'}
                              </button>
                            </td>
                            <td className='py-3 pl-2'>
                              {item.pictures?.length ? (
                                <div className='flex gap-1 flex-wrap'>
                                  {item.pictures.slice(0, 3).map((url, i) => (
                                    <a key={i} href={url} target='_blank' rel='noopener noreferrer'
                                      className='inline-flex items-center justify-center w-8 h-8 rounded border border-gray-200 bg-gray-50 hover:border-primary/30'>
                                      <ImageIcon className='h-4 w-4 text-gray-500' />
                                    </a>
                                  ))}
                                  {item.pictures.length > 3 && <span className='text-xs text-gray-500'>+{item.pictures.length - 3}</span>}
                                </div>
                              ) : <span className='text-gray-400'>—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {finesPagination && finesPagination.totalPages > 1 && (
                    <div className='flex items-center justify-between pt-2 border-t border-gray-100'>
                      <span className='text-xs text-gray-500'>
                        Page {finesPagination.currentPage} of {finesPagination.totalPages} · {finesPagination.totalItems} total
                      </span>
                      <div className='flex gap-2'>
                        <button onClick={() => setFinesPage(p => p - 1)} disabled={!finesPagination.hasPrevious}
                          className='px-3 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'>
                          Previous
                        </button>
                        <button onClick={() => setFinesPage(p => p + 1)} disabled={!finesPagination.hasNext}
                          className='px-3 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'>
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Facility Registration */}
          {activeTab === 'facility-registration' && (
            <div className='bg-white rounded-lg border border-gray-200 p-4 sm:p-6 space-y-4'>
              {/* Header */}
              <div className='flex items-start justify-between'>
                <div>
                  <h3 className='text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2'>
                    <Building2 className='h-4 w-4 text-primary' />
                    {dictionary['dashboard.facility_registration'] ?? 'Facility Registration'}
                  </h3>
                  <p className='text-xs text-gray-600 mt-0.5'>
                    {dictionary['dashboard.facility_registration_description'] ?? 'Distribution of registered facilities by location.'}
                  </p>
                </div>
                {(facilityRegStack.length > 0 || facilityRegFacilityTypeId || facilityRegLocationType || facilityRegStartDate || facilityRegEndDate) && (
                  <button
                    onClick={() => {
                      setFacilityRegStack([]);
                      setFacilityRegFacilityTypeId('');
                      setFacilityRegLocationType('');
                      setFacilityRegStartDate('');
                      setFacilityRegEndDate('');
                      setFacilityRegModalLocation(null);
                    }}
                    className='text-xs text-primary hover:underline flex items-center gap-1'
                  >
                    <X className='h-3 w-3' /> {dictionary['dashboard.facility_reg_reset'] ?? 'Reset'}
                  </button>
                )}
              </div>

              {/* Filters */}
              <div className='grid grid-cols-2 sm:grid-cols-4 gap-3'>
                <Input
                  type='select'
                  label={dictionary['dashboard.facility_type'] ?? 'Facility Type'}
                  value={facilityRegFacilityTypeId}
                  onChange={e => { setFacilityRegFacilityTypeId(String(e.target.value)); setFacilityRegModalLocation(null); }}
                  placeholder={dictionary['dashboard.facility_reg_all_types'] ?? 'All types'}
                  options={[
                    { value: '', label: dictionary['dashboard.facility_reg_all_types'] ?? 'All types' },
                    ...facilityTypes.map(ft => ({ value: ft.id, label: getLocalizedFacilityTypeName(ft.id, facilityTypes, locale) })),
                  ]}
                />
                <Input
                  type='select'
                  label={dictionary['dashboard.group_by_level'] ?? 'Group by Level'}
                  value={facilityRegLocationType}
                  onChange={e => { setFacilityRegLocationType(String(e.target.value)); setFacilityRegStack([]); setFacilityRegModalLocation(null); }}
                  placeholder={dictionary['dashboard.default'] ?? 'Default'}
                  options={[
                    { value: '', label: dictionary['dashboard.default'] ?? 'Default' },
                    { value: 'PROVINCE', label: dictionary['dashboard.province'] ?? 'Province' },
                    { value: 'DISTRICT', label: dictionary['dashboard.district'] ?? 'District' },
                    { value: 'SECTOR', label: dictionary['dashboard.sector'] ?? 'Sector' },
                    { value: 'CELL', label: dictionary['dashboard.cell'] ?? 'Cell' },
                    { value: 'VILLAGE', label: dictionary['dashboard.village'] ?? 'Village' },
                  ]}
                />
                <Input
                  type='date'
                  label={dictionary['dashboard.start_date'] ?? 'Start Date'}
                  value={facilityRegStartDate}
                  onChange={e => setFacilityRegStartDate(e.target.value)}
                />
                <Input
                  type='date'
                  label={dictionary['dashboard.end_date'] ?? 'End Date'}
                  value={facilityRegEndDate}
                  onChange={e => setFacilityRegEndDate(e.target.value)}
                />
              </div>

              {/* Breadcrumb */}
              {facilityRegStack.length > 0 && (
                <div className='flex items-center gap-1 text-xs text-gray-500 flex-wrap'>
                  <button className='hover:text-primary flex items-center gap-0.5' onClick={() => { setFacilityRegStack([]); setFacilityRegModalLocation(null); }}>
                    <MapPin className='h-3 w-3' />{dictionary['dashboard.all'] ?? 'All'}
                  </button>
                  {facilityRegStack.map((item, i) => (
                    <span key={item.id} className='flex items-center gap-1'>
                      <ChevronRight className='h-3 w-3' />
                      <button className='hover:text-primary' onClick={() => { setFacilityRegStack(prev => prev.slice(0, i + 1)); setFacilityRegModalLocation(null); }}>
                        {item.name}
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Chart + ranked list */}
              {facilityRegLoading ? (
                <div className='flex justify-center py-12'>
                  <div className='h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-primary' />
                </div>
              ) : !facilityRegData?.locations.length ? (
                <p className='text-sm text-gray-500 text-center py-8'>{dictionary['dashboard.no_data_for_filters'] ?? 'No data available for the selected filters.'}</p>
              ) : (
                <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                  {/* Donut chart */}
                  <PieChart
                    labels={facilityRegData.locations.map(l => l.locationName)}
                    series={facilityRegData.locations.map(l => l.totalFacilities)}
                    colors={facilityRegData.locations.map((_, i) => {
                      const p = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'];
                      return p[i % p.length];
                    })}
                    width={360}
                  />

                  {/* Ranked list */}
                  <div className='space-y-1.5 max-h-[420px] overflow-y-auto pr-1'>
                    <p className='text-xs text-gray-500 mb-2 sticky top-0 bg-white pb-1'>
                      {facilityRegData.parentLocationName
                        ? `${facilityRegData.parentLocationName} — `
                        : ''}
                      <span className='font-medium text-gray-700'>{facilityRegData.totalFacilities}</span> {dictionary['dashboard.total_facilities_count'] ?? 'total facilities'}
                    </p>
                    {facilityRegData.locations.map((loc, i) => {
                      const palette = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'];
                      const color = palette[i % palette.length];
                      return (
                        <button
                          key={loc.locationId}
                          type='button'
                          onClick={() => setFacilityRegModalLocation(loc)}
                          className='w-full text-left p-2.5 rounded-lg border border-gray-100 hover:border-primary/30 hover:bg-secondary/50 transition-colors flex items-center justify-between gap-3'
                        >
                          <div className='flex items-center gap-2 min-w-0'>
                            <span className='w-2.5 h-2.5 rounded-full flex-shrink-0' style={{ backgroundColor: color }} />
                            <span className='text-xs font-medium text-gray-900 truncate'>{loc.locationName}</span>
                          </div>
                          <div className='flex items-center gap-1.5 flex-shrink-0'>
                            <span className='text-sm font-bold text-gray-800'>{loc.totalFacilities}</span>
                            <ChevronRight className='h-3.5 w-3.5 text-gray-400' />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Collect fine confirm modal */}
              {collectConfirm && (
                <div className='fixed inset-0 bg-black/40 z-50 flex items-center justify-center' onClick={() => setCollectConfirm(null)}>
                  <div className='bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4' onClick={e => e.stopPropagation()}>
                    <h3 className='text-base font-semibold text-gray-900 mb-1'>Confirm Collection</h3>
                    <p className='text-sm text-gray-600 mb-1'>
                      Mark the fine for <span className='font-medium text-gray-900'>{collectConfirm.facilityTitle}</span> as collected?
                    </p>
                    <p className='text-sm text-amber-700 font-semibold mb-5'>
                      {collectConfirm.fineAmount?.toLocaleString()} RWF
                    </p>
                    <div className='flex gap-3 justify-end'>
                      <button
                        onClick={() => setCollectConfirm(null)}
                        className='px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50'>
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          markFineCollected.mutate({ id: collectConfirm.id, collected: true });
                          setCollectConfirm(null);
                        }}
                        disabled={markFineCollected.isPending}
                        className='px-4 py-2 text-sm rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-60'>
                        Confirm Collection
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Location detail modal */}
              {facilityRegModalLocation && (
                <div className='fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center' onClick={() => setFacilityRegModalLocation(null)}>
                  <div
                    className='bg-white rounded-xl shadow-xl flex flex-col'
                    style={{ maxHeight: '90vh', width: '80vw' }}
                    onClick={e => e.stopPropagation()}
                  >
                    {/* Header */}
                    <div className='flex items-start justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0'>
                      <div>
                        <h3 className='text-base font-semibold text-gray-900'>{facilityRegModalLocation.locationName}</h3>
                        <p className='text-xs text-gray-500 mt-0.5'>
                          <span className='text-xl font-bold text-gray-800 mr-1'>{facilityRegModalLocation.totalFacilities}</span>
                          {dictionary['dashboard.facilities_registered'] ?? 'facilities registered'}
                        </p>
                      </div>
                      <button onClick={() => setFacilityRegModalLocation(null)} className='text-gray-400 hover:text-gray-600 mt-0.5'>
                        <X className='h-4 w-4' />
                      </button>
                    </div>

                    {/* Type filter chips */}
                    {facilityRegModalLocation.byFacilityType && facilityRegModalLocation.byFacilityType.length > 0 && (
                      <div className='px-5 py-3 border-b border-gray-100 flex-shrink-0'>
                        <div className='flex flex-wrap gap-1.5'>
                          <button
                            type='button'
                            onClick={() => { setFacilityRegModalTypeFilter(''); setFacilityRegModalPage(1); }}
                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${!facilityRegModalTypeFilter ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          >
                            {dictionary['dashboard.all'] ?? 'All'} ({facilityRegModalLocation.totalFacilities})
                          </button>
                          {facilityRegModalLocation.byFacilityType.map(ft => (
                            <button
                              key={ft.facilityTypeId}
                              type='button'
                              onClick={() => { setFacilityRegModalTypeFilter(ft.facilityTypeId); setFacilityRegModalPage(1); }}
                              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${facilityRegModalTypeFilter === ft.facilityTypeId ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            >
                              {ft.facilityTypeName} ({ft.count})
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Search */}
                    <div className='px-5 py-2.5 border-b border-gray-100 flex-shrink-0'>
                      <Input
                        type='text'
                        placeholder={dictionary['dashboard.search_placeholder'] ?? 'Search by name or ID…'}
                        value={facilityRegModalSearch}
                        onChange={e => { setFacilityRegModalSearch(e.target.value); setFacilityRegModalPage(1); }}
                        inputPrefix={<Search className='h-3.5 w-3.5' />}
                      />
                    </div>

                    {/* Facility list */}
                    <div className='overflow-y-auto flex-1'>
                      {facilityRegFacilitiesLoading ? (
                        <div className='flex justify-center py-10'>
                          <div className='h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-primary' />
                        </div>
                      ) : !facilityRegFacilities?.data.length ? (
                        <p className='text-sm text-gray-400 text-center py-10'>{dictionary['dashboard.no_facilities_found'] ?? 'No facilities found.'}</p>
                      ) : (
                        <table className='w-full text-sm'>
                          <thead className='sticky top-0 bg-white border-b border-gray-100'>
                            <tr className='text-left text-[11px] text-gray-400 font-medium'>
                              <th className='px-5 py-1.5'>{dictionary['dashboard.fines_facility'] ?? 'Facility'}</th>
                              <th className='px-5 py-1.5 hidden sm:table-cell'>{dictionary['dashboard.type'] ?? 'Type'}</th>
                              <th className='px-5 py-1.5 hidden sm:table-cell'>{dictionary['dashboard.contact'] ?? 'Contact'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {facilityRegFacilities.data.map(fac => (
                              <tr key={fac.id} className='border-b border-gray-50 hover:bg-gray-50'>
                                <td className='px-5 py-1.5'>
                                  <div className='text-xs font-medium text-gray-900 leading-tight'>{fac.title}</div>
                                  <div className='text-[10px] text-gray-400'>{fac.identifierNumber}</div>
                                </td>
                                <td className='px-5 py-1.5 hidden sm:table-cell'>
                                  <span className='text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded'>
                                    {locale === 'rw' ? fac.facilityType?.nameKinyarwanda : locale === 'fr' ? fac.facilityType?.nameFrench : fac.facilityType?.name}
                                  </span>
                                </td>
                                <td className='px-5 py-1.5 text-xs text-gray-500 hidden sm:table-cell'>{fac.contactNumber ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {/* Footer: pagination + drill down */}
                    <div className='flex items-center justify-between px-5 py-3 border-t border-gray-100 flex-shrink-0 gap-3'>
                      <div className='flex items-center gap-1.5'>
                        {(facilityRegFacilities?.total ?? 0) > 15 && (
                          <>
                            <button
                              onClick={() => setFacilityRegModalPage(p => Math.max(1, p - 1))}
                              disabled={facilityRegModalPage <= 1}
                              className='px-2.5 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50'
                            >{dictionary['dashboard.prev'] ?? 'Prev'}</button>
                            <span className='text-xs text-gray-500'>
                              {facilityRegModalPage} / {Math.ceil((facilityRegFacilities?.total ?? 0) / 15)}
                            </span>
                            <button
                              onClick={() => setFacilityRegModalPage(p => p + 1)}
                              disabled={facilityRegModalPage * 15 >= (facilityRegFacilities?.total ?? 0)}
                              className='px-2.5 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50'
                            >{dictionary['dashboard.next'] ?? 'Next'}</button>
                          </>
                        )}
                      </div>
                      <button
                        type='button'
                        onClick={() => {
                          setFacilityRegStack(prev => [...prev, { id: facilityRegModalLocation.locationId, name: facilityRegModalLocation.locationName }]);
                          setFacilityRegModalLocation(null);
                        }}
                        className='flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary border border-primary/30 rounded-lg hover:bg-secondary transition-colors'
                      >
                        <ChevronRight className='h-4 w-4' /> {dictionary['dashboard.drill_down'] ?? 'Drill Down'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}

          {/* Decisions */}
          {activeTab === 'decisions' && (
            <DecisionsPanel assessmentType='HYGIENE' />
          )}

          {/* Recommendations */}
          {activeTab === 'recommendations' && (
            <RecommendationsPanel assessmentType='HYGIENE' />
          )}

          {/* Overview */}
          {activeTab === 'overview' && (
            <div className='space-y-3 sm:space-y-4'>
              {/* KPI Cards */}
              {overviewData ? (
                <OverviewCards
                  data={overviewData}
                  isLoading={overviewLoading}
                  kpiData={kpiData}
                  kpiLoading={kpiLoading}
                  kpiType={kpiType}
                  onKpiTypeChange={setKpiType}
                />
              ) : (
                <OverviewCards data={overviewData!} isLoading={true} kpiLoading={true} />
              )}

              {/* Activity Trends — first 3 charts */}
              <ActivityTrends limit={3} filters={{ month: filters.month, type: 'HYGIENE', facilityTypeId: filters.facilityTypeId, geoLocationId: filters.geoLocationId }} />

              {/* Map + Facility Type Performance — side by side */}
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-3 items-start'>

                {/* Map */}
                <div className='bg-white rounded-lg p-5 shadow-sm flex flex-col gap-3 relative z-0' style={{ border: '1px solid var(--nhip-border)' }}>
                  <div className='flex items-center gap-2'>
                    <div className='w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border' style={{ backgroundColor: 'var(--nhip-surface)', borderColor: 'var(--nhip-border)' }}>
                  <MapPin className='w-4 h-4' style={{ color: 'var(--nhip-ink-muted)' }} />
                    </div>
                    <div>
                      <p className='text-sm font-semibold' style={{ color: 'var(--nhip-ink)' }}>{dictionary['dashboard.facilities_map']}</p>
                      <p className='text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>{dictionary['dashboard.map_description']}</p>
                    </div>
                  </div>
                  <RwandaMap height={340} />
                  <div className='flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-2' style={{ borderTop: '1px solid var(--nhip-border)' }}>
                    {[
                      { hex: '#22c55e', label: `${dictionary['dashboard.excellent']} 90–100%` },
                      { hex: '#eab308', label: `${dictionary['dashboard.good']} 75–89%` },
                      { hex: '#f97316', label: `${dictionary['dashboard.fair']} 60–74%` },
                      { hex: '#ef4444', label: `${dictionary['dashboard.poor']} <60%` },
                    ].map(l => (
                      <div key={l.hex} className='flex items-center gap-1.5 text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>
                        <div className='w-2.5 h-2.5 rounded-full flex-shrink-0' style={{ backgroundColor: l.hex }} />
                        {l.label}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Facility Type Performance */}
                <div className='bg-white rounded-lg p-5 shadow-sm flex flex-col gap-3' style={{ border: '1px solid var(--nhip-border)' }}>
                  <div className='flex items-center justify-between gap-3'>
                    <div className='flex items-center gap-2'>
                      <div className='w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border' style={{ backgroundColor: 'var(--nhip-surface)', borderColor: 'var(--nhip-border)' }}>
                      <BarChart3 className='w-4 h-4' style={{ color: 'var(--nhip-ink-muted)' }} />
                      </div>
                      <div>
                        <p className='text-sm font-semibold' style={{ color: 'var(--nhip-ink)' }}>{dictionary['dashboard.facility_type_performance']}</p>
                        <p className='text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>Avg hygiene score · sorted highest to lowest</p>
                      </div>
                    </div>
                    {facilityTypeMetrics.length > 0 && (
                      <div className='relative flex-shrink-0' ref={exportDropdownRef}>
                        <button
                          onClick={() => setShowExportDropdown(!showExportDropdown)}
                          disabled={exportLoading !== null}
                          className='flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg transition-colors hover:bg-gray-50'
                          style={{ border: '1px solid var(--nhip-border)', color: 'var(--nhip-ink-muted)' }}
                        >
                          <Download className='h-3.5 w-3.5' />
                          {exportLoading ? dictionary['common.exporting'] : dictionary['common.export']}
                        </button>
                        {showExportDropdown && (
                          <div className='absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-lg z-50' style={{ border: '1px solid var(--nhip-border)' }}>
                            <div className='p-1.5'>
                              <button onClick={handleExportFacilityTypesExcel} disabled={exportLoading !== null}
                                className='w-full flex items-center gap-2 px-3 py-2 text-xs rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50'
                                style={{ color: 'var(--nhip-ink)' }}>
                                <FileText className='h-3.5 w-3.5 text-green-600' />Export to Excel
                              </button>
                              <button onClick={handleExportFacilityTypesPDF} disabled={exportLoading !== null}
                                className='w-full flex items-center gap-2 px-3 py-2 text-xs rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50'
                                style={{ color: 'var(--nhip-ink)' }}>
                                <FileText className='h-3.5 w-3.5 text-red-500' />Export to PDF
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {analyticsLoading ? (
                    <div className='space-y-2'>
                      {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className='flex items-center gap-3 animate-pulse'>
                          <div className='h-3 rounded w-32' style={{ backgroundColor: 'var(--nhip-border)' }} />
                          <div className='flex-1 h-4 rounded' style={{ backgroundColor: 'var(--nhip-border)', opacity: 0.6 - i * 0.05 }} />
                          <div className='h-3 rounded w-10' style={{ backgroundColor: 'var(--nhip-border)' }} />
                        </div>
                      ))}
                    </div>
                  ) : facilityTypeMetrics.length > 0 ? (() => {
                    const sorted = [...facilityTypeMetrics]
                      .filter(t => t.totalAssessments > 0)
                      .sort((a, b) => b.averageScore - a.averageScore)
                      .map(t => ({
                        name: getLocalizedFacilityTypeName(t.facilityTypeId, facilityTypes, locale) || t.facilityTypeId,
                        score: parseFloat(t.averageScore.toFixed(1)),
                        facilities: t.totalFacilities,
                        assessments: t.totalAssessments,
                      }));
                    const barH = 24;
                    const chartH = Math.max(200, sorted.length * (barH + 10));
                    const yAxisW = Math.min(180, Math.max(100, sorted.reduce((m, d) => Math.max(m, d.name.length * 6), 0)));
                    return (
                      <div style={{ height: Math.min(440, chartH), overflowY: sorted.length > 16 ? 'auto' : 'visible' }}>
                        <div style={{ height: chartH, minWidth: 0 }}>
                          <ReactECharts
                            option={buildFacilityRankingBarOption(sorted, yAxisW, barH, '#E2ECE6')}
                            style={{ height: '100%', width: '100%' }}
                            opts={{ renderer: 'svg' }}
                          />
                        </div>
                      </div>
                    );
                  })() : (
                    <div className='text-center py-12 text-sm' style={{ color: 'var(--nhip-ink-muted)' }}>{dictionary['dashboard.no_facility_data']}</div>
                  )}

                  {!analyticsLoading && facilityTypeMetrics.length > 0 && (
                    <div className='flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-2' style={{ borderTop: '1px solid var(--nhip-border)' }}>
                      {[
                        { hex: '#22c55e', label: 'Excellent ≥90%' },
                        { hex: '#eab308', label: 'Good 75–89%' },
                        { hex: '#f97316', label: 'Fair 60–74%' },
                        { hex: '#ef4444', label: 'Poor <60%' },
                      ].map(l => (
                        <div key={l.label} className='flex items-center gap-1.5 text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>
                          <div className='w-2.5 h-2.5 rounded-sm flex-shrink-0' style={{ backgroundColor: l.hex }} />
                          {l.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>

              {/* Questionnaire Analytics */}
              {questionnaires && questionnaires.length > 0 && (
                <div className='\ rounded-lg border border-gray-200 p-4 sm:p-6'>
                  <div className='mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
                    <div>
                      <h3 className='text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2'>
                        <PieChartIcon className='h-4 w-4' />{dictionary['dashboard.questionnaire_analytics']}
                      </h3>
                      <p className='text-xs text-gray-600 mt-1'>{dictionary['dashboard.question_analytics_description']}</p>
                    </div>
                    {selectedQuestionnaireId && questionnaireAnalytics?.questions.length > 0 && (
                      <div className='flex flex-col sm:flex-row gap-2'>
                        <button onClick={handleExportQuestionnaireAnalytics}
                          className='flex items-center gap-2 px-3 py-1.5 text-xs sm:text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors justify-center'>
                          <Download className='h-3.5 w-3.5' /><span>Summary</span>
                        </button>
                        <button
                          onClick={handleExportFacilityResponses}
                          disabled={exportingFacilityResponses}
                          className='flex items-center gap-2 px-3 py-1.5 text-xs sm:text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors justify-center disabled:opacity-50'
                        >
                          <Download className='h-3.5 w-3.5' />
                          <span>{exportingFacilityResponses ? 'Exporting…' : 'Facility Responses'}</span>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Questionnaire tabs */}
                  <div className='mb-4 border-b border-gray-200'>
                    <nav className='flex gap-1 overflow-x-auto py-1'>
                      {questionnaires.map(q => (
                        <button key={q.id} type='button' onClick={() => setSelectedQuestionnaireId(q.id)}
                          title={q.name}
                          className={`flex-shrink-0 px-3 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors whitespace-nowrap max-w-[200px] sm:max-w-[280px] truncate ${selectedQuestionnaireId === q.id
                            ? 'border-blue-600 text-blue-600 bg-blue-50'
                            : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}>
                          {q.name}
                        </button>
                      ))}
                    </nav>
                  </div>

                  {/* Redesigned: Harmonized Questionnaire filters */}
                  {selectedQuestionnaireId && (
                    <div className="mb-4 bg-white border border-gray-200 rounded-xl px-4 py-4 sm:p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Filter className="h-4 w-4 text-blue-600" />
                          <span className="text-[13px] sm:text-sm font-semibold text-blue-900">{dictionary['dashboard.filter_analytics']}</span>
                        </div>
                        {hasQuestionnaireFilters && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={clearQuestionnaireFilters}
                            className="flex items-center gap-1 h-8 text-xs sm:text-sm border border-blue-200 text-blue-700 hover:bg-blue-50"
                          >
                            <X className="h-4 w-4" />
                            <span className="sm:inline hidden">{dictionary['dashboard.clear_filters']}</span>
                            <span className="sm:hidden">Clear</span>
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Location Selector (harmonized label) */}
                        <div className="flex flex-col">
                          <span className="text-xs font-semibold text-gray-600 mb-1">
                            {dictionary['dashboard.filter_by_location']}
                          </span>
                          <div>
                            <SingleLocationSelector
                              selectedLocationId={questionnaireFilters.locationId}
                              onLocationChange={id => handleQuestionnaireFilterChange('locationId', id)}
                            />
                          </div>
                        </div>
                        {/* Start Date (harmonized input UI) */}
                        <div className="flex flex-col">
                          <Input
                            type="date"
                            label={dictionary['dashboard.start_date']}
                            value={questionnaireFilters.startDate || ''}
                            onChange={e => handleQuestionnaireFilterChange('startDate', e.target.value)}
                            className="w-full"
                          />
                        </div>
                        {/* End Date (harmonized input UI) */}
                        <div className="flex flex-col">
                          <Input
                            type="date"
                            label={dictionary['dashboard.end_date']}
                            value={questionnaireFilters.endDate || ''}
                            onChange={e => handleQuestionnaireFilterChange('endDate', e.target.value)}
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Questionnaire analytics display */}
                  {selectedQuestionnaireId && (
                    <>
                      {questionnaireAnalyticsLoading ? (
                        <div className='flex items-center justify-center py-12'>
                          <div className='h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600' />
                        </div>
                      ) : questionnaireAnalytics ? (
                        <div className='space-y-4'>
                          <div className='bg-blue-50 rounded-lg p-3 border border-blue-200'>
                            <h3 className='text-sm font-medium text-blue-900 mb-1'>{questionnaireAnalytics.questionnaireName}</h3>
                            <div className='flex flex-col sm:flex-row gap-2 sm:gap-4 text-xs text-blue-700'>
                              <span>{dictionary['dashboard.facility_type_label']}: {questionnaireAnalytics.facilityTypeName}</span>
                              <span>{dictionary['dashboard.total_assessments_label']}: {questionnaireAnalytics.totalAssessments}</span>
                            </div>
                          </div>
                          <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                            {questionnaireAnalytics.questions.map((question: QuestionAnalyticsDto, index: number) => (
                              <div
                                key={question.questionId}
                                className='border bg-white rounded-lg p-3 sm:p-4 cursor-pointer hover:border-primary/50 hover:shadow-sm transition-all'
                                onClick={() => setSelectedQuestion({ question, index })}
                              >
                                <div className='mb-3'>
                                  <h4 className='text-sm font-medium text-gray-900 mb-1'>
                                    {dictionary['dashboard.question']}{index + 1}: {question.questionText}
                                  </h4>
                                  <div className='text-xs text-gray-500'>
                                    {dictionary['dashboard.type_label']}: {question.questionType} • {dictionary['dashboard.weight']}: {question.weight} • {dictionary['dashboard.responses']}: {question.totalResponses}
                                  </div>
                                </div>
                                <div className='grid grid-cols-2 gap-2 mb-3'>
                                  {question.options.map(opt => (
                                    <div key={opt.optionId} className='text-center p-2 bg-gray-50 rounded-lg'>
                                      <div className={`text-base sm:text-lg font-bold ${getPercentageColor(opt.percentage)}`}>{opt.percentage.toFixed(1)}%</div>
                                      <div className='text-xs text-gray-600 mt-0.5 break-words'>{opt.optionText}</div>
                                      <div className='text-xs text-gray-500 mt-0.5'>{opt.selectionCount} / {question.totalResponses}</div>
                                    </div>
                                  ))}
                                </div>
                                <div className="flex flex-col md:flex-row justify-center my-3 gap-4">
                                  <div className="w-full md:w-2/3 flex items-center justify-center">
                                    <PieChart
                                      title={dictionary['dashboard.response_distribution']}
                                      labels={question.options.map(o => o.optionText)}
                                      series={question.options.map(o => o.selectionCount)}
                                      colors={question.options.map(o =>
                                        o.percentage >= 80 ? '#10B981' : o.percentage >= 60 ? '#F59E0B' : o.percentage >= 40 ? '#FB923C' : '#EF4444'
                                      )}
                                      width={240}
                                    />
                                  </div>

                                  {question.options.length === 2 && (
                                    <div className="w-full md:w-1/3 flex items-stretch">
                                      <div className='mt-3 md:mt-0 p-2  w-full flex flex-col justify-center'>
                                        <h5 className='text-xs font-medium text-blue-900 mb-1'>{dictionary['dashboard.compliance_status']}</h5>
                                        {question.options.filter(o => o.marks > 0).map(opt => (
                                          <div key={opt.optionId} className='flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2'>

                                            <span className='text-xs text-gray-700'>
                                              <span className={`text-base font-bold ${getPercentageColor(opt.percentage)}`}>{opt.percentage.toFixed(1)}%</span> &nbsp;
                                              {dictionary['dashboard.meets_requirement']} ({opt.selectionCount} {dictionary['dashboard.out_of']} {question.totalResponses})
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className='mt-3 pt-3 border-t border-gray-200'>
                                  {loadingQuestionBreakdownId === question.questionId ? (
                                    <div className='flex items-center justify-center py-2 text-xs text-gray-500'>
                                      <span className='inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 mr-2' />
                                      {dictionary['dashboard.generating'] ?? 'Generating…'}
                                    </div>
                                  ) : questionBreakdownByQuestionId[question.questionId] ? (
                                    <>
                                      <div className='flex items-center justify-between mb-2'>
                                        <h5 className='text-xs font-medium text-gray-700'>{dictionary['dashboard.who_responded'] ?? 'Who responded'}</h5>
                                        <button
                                          onClick={() => handleExportQuestionBreakdown(question.questionId)}
                                          className='flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded'
                                        >
                                          <Download className='h-3 w-3' />{dictionary['dashboard.download_breakdown'] ?? 'Download'}
                                        </button>
                                      </div>

                                    </>
                                  ) : (
                                    <Button
                                      type='button'
                                      variant='outline'
                                      size='sm'
                                      onClick={() => handleGenerateQuestionBreakdown(question.questionId)}
                                      className='w-full text-xs'
                                    >
                                      {dictionary['dashboard.generate_breakdown'] ?? 'Generate who responded Yes/No'}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Question detail modal */}
                          <Dialog open={selectedQuestion !== null} onOpenChange={open => { if (!open) setSelectedQuestion(null); }}>
                            {selectedQuestion && (
                              <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
                                <DialogHeader>
                                  <div className='flex items-center gap-2'>
                                    <span className='text-xs font-bold text-primary bg-primary/10 px-2.5 py-0.5 rounded-md flex-shrink-0'>
                                      {dictionary['dashboard.question']}{selectedQuestion.index + 1}
                                    </span>
                                    <DialogTitle className='text-sm leading-snug'>{selectedQuestion.question.questionText}</DialogTitle>
                                  </div>
                                  <p className='text-xs text-gray-400 pt-1'>
                                    {dictionary['dashboard.type_label']}: {selectedQuestion.question.questionType} · {dictionary['dashboard.weight']}: {selectedQuestion.question.weight} · {dictionary['dashboard.responses']}: {selectedQuestion.question.totalResponses}
                                  </p>
                                </DialogHeader>

                                <div className='grid grid-cols-2 gap-2'>
                                  {selectedQuestion.question.options.map(opt => (
                                    <div key={opt.optionId} className='text-center p-3 bg-gray-50 rounded-lg'>
                                      <div className={`text-lg font-bold ${getPercentageColor(opt.percentage)}`}>{opt.percentage.toFixed(1)}%</div>
                                      <div className='text-xs text-gray-600 mt-0.5 break-words'>{opt.optionText}</div>
                                      <div className='text-xs text-gray-400 mt-0.5'>{opt.selectionCount} / {selectedQuestion.question.totalResponses}</div>
                                    </div>
                                  ))}
                                </div>

                                <div className='flex flex-col md:flex-row justify-center gap-4'>
                                  <div className='flex-1 flex items-center justify-center'>
                                    <PieChart
                                      title={dictionary['dashboard.response_distribution']}
                                      labels={selectedQuestion.question.options.map(o => o.optionText)}
                                      series={selectedQuestion.question.options.map(o => o.selectionCount)}
                                      colors={selectedQuestion.question.options.map(o =>
                                        o.percentage >= 80 ? '#10B981' : o.percentage >= 60 ? '#F59E0B' : o.percentage >= 40 ? '#FB923C' : '#EF4444'
                                      )}
                                      width={260}
                                    />
                                  </div>
                                  {selectedQuestion.question.options.length === 2 && (
                                    <div className='flex-1 flex items-center'>
                                      <div className='bg-blue-50 rounded-lg p-4 w-full'>
                                        <h5 className='text-xs font-semibold text-blue-800 mb-2'>{dictionary['dashboard.compliance_status']}</h5>
                                        {selectedQuestion.question.options.filter(o => o.marks > 0).map(opt => (
                                          <p key={opt.optionId} className='text-xs text-gray-700'>
                                            <span className={`text-lg font-bold ${getPercentageColor(opt.percentage)}`}>{opt.percentage.toFixed(1)}%</span>
                                            {' '}{dictionary['dashboard.meets_requirement']}
                                            <span className='text-gray-400'> ({opt.selectionCount} {dictionary['dashboard.out_of']} {selectedQuestion.question.totalResponses})</span>
                                          </p>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <div className='pt-3 border-t border-gray-100'>
                                  {loadingQuestionBreakdownId === selectedQuestion.question.questionId ? (
                                    <div className='flex items-center justify-center py-3 text-xs text-gray-500 gap-2'>
                                      <span className='h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500' />
                                      {dictionary['dashboard.generating'] ?? 'Generating…'}
                                    </div>
                                  ) : questionBreakdownByQuestionId[selectedQuestion.question.questionId] ? (
                                    <div className='flex items-center justify-between'>
                                      <span className='text-xs font-medium text-gray-600'>{dictionary['dashboard.who_responded'] ?? 'Who responded'}</span>
                                      <button
                                        onClick={() => handleExportQuestionBreakdown(selectedQuestion.question.questionId)}
                                        className='flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors'
                                      >
                                        <Download className='h-3 w-3' />{dictionary['dashboard.download_breakdown'] ?? 'Download'}
                                      </button>
                                    </div>
                                  ) : (
                                    <Button
                                      type='button'
                                      variant='outline'
                                      size='sm'
                                      onClick={() => handleGenerateQuestionBreakdown(selectedQuestion.question.questionId)}
                                      className='w-full text-xs'
                                    >
                                      {dictionary['dashboard.generate_breakdown'] ?? 'Generate who responded Yes/No'}
                                    </Button>
                                  )}
                                </div>
                              </DialogContent>
                            )}
                          </Dialog>
                        </div>
                      ) : (
                        <div className='text-center py-8 text-gray-500'>
                          <PieChartIcon className='h-8 w-8 mx-auto mb-2 text-gray-400' />
                          <p className='text-sm'>{dictionary['dashboard.no_data_message']}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Enforcement this month */}
              <div className='w-full lg:w-1/2'>
                <EnforcementPanel filters={{ month: filters.month, type: 'HYGIENE', facilityTypeId: filters.facilityTypeId, geoLocationId: filters.geoLocationId }} />
              </div>

              {/* Bottom row: Facility Type Performance (left) + Follow-up & Top Locations stacked (right) */}
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-3 items-start'>
                <FacilityTypePerformanceTable filters={{ month: filters.month, type: 'HYGIENE', geoLocationId: filters.geoLocationId }} />
                <div className='flex flex-col gap-3'>
                  <FollowupCompletion filters={{ month: filters.month, facilityTypeId: filters.facilityTypeId, geoLocationId: filters.geoLocationId }} />
                  <TopLocations filters={{ month: filters.month, type: 'HYGIENE', facilityTypeId: filters.facilityTypeId, geoLocationId: filters.geoLocationId }} />
                </div>
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}
